import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Avatar,
    Box,
    Chip,
    Paper,
    Slide,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Icon } from '@iconify/react/dist/iconify.js';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const ViewUserDetailsModal = ({ open, handleClose, user }) => {
    if (!user) return null;

    const getValue = (value) => value && value !== '-' ? value : 'N/A';

    const InfoRow = ({ icon, label, value, isLink = false }) => (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1 }}>
            <Box sx={{
                color: 'primary.main',
                mt: 0.3,
                minWidth: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                {icon}
            </Box>
            <Box sx={{ flex: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.3, fontWeight: 500 }}>
                    {label}
                </Typography>
                {isLink && value !== 'N/A' ? (
                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main', wordBreak: 'break-word' }}>
                        {value}
                    </Typography>
                ) : (
                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary', wordBreak: 'break-word' }}>
                        {value}
                    </Typography>
                )}
            </Box>
        </Box>
    );

    const SectionCard = ({ title, icon, children }) => (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                mb: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
                '&:hover': {
                    borderColor: 'primary.light',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                },
                transition: 'all 0.2s ease-in-out'
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ color: 'primary.main' }}>{icon}</Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {title}
                </Typography>
            </Box>
            {children}
        </Paper>
    );

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            TransitionComponent={Transition}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    maxHeight: '90vh',
                    overflow: 'hidden'
                }
            }}
        >
            {/* Header with Gradient Background - Matching SocietyDetailsModal */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #487fff 0%, #6366f1 100%)',
                    color: 'white',
                    position: 'relative',
                }}
            >
                {/* Background Pattern */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        opacity: 0.1,
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    }}
                />

                <DialogTitle sx={{ p: 2.5, pb: 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                            User Details
                        </Typography>
                        <IconButton
                            aria-label="close"
                            onClick={handleClose}
                            size="small"
                            sx={{
                                color: 'white',
                                backgroundColor: 'rgba(255,255,255,0.15)',
                                '&:hover': {
                                    backgroundColor: 'rgba(255,255,255,0.25)',
                                },
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </DialogTitle>

                {/* Profile Section in Header */}
                <Box sx={{ px: 2.5, pb: 2.5, pt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Avatar
                            sx={{
                                width: 72,
                                height: 72,
                                bgcolor: 'white',
                                color: 'primary.main',
                                fontSize: '1.75rem',
                                fontWeight: 700,
                                boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
                            }}
                        >
                            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 200 }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', mb: 0.5 }}>
                                {getValue(user?.name)}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Chip
                                    label={`ID: ${getValue(user?.id)}`}
                                    size="small"
                                    sx={{
                                        bgcolor: 'rgba(255,255,255,0.2)',
                                        color: 'white',
                                        fontWeight: 500,
                                        fontSize: '0.75rem',
                                    }}
                                />
                                <Chip
                                    label={user?.status ? 'Active' : 'Inactive'}
                                    size="small"
                                    sx={{
                                        bgcolor: user?.status ? '#22c55e' : '#ef4444',
                                        color: 'white',
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                    }}
                                />
                                <Chip
                                    label={user?.userAccess ? 'Blocked' : 'Unblocked'}
                                    size="small"
                                    sx={{
                                        bgcolor: user?.userAccess ? '#f97316' : '#22c55e',
                                        color: 'white',
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                    }}
                                />
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <DialogContent sx={{ p: 0, bgcolor: '#f8fafc' }}>
                {/* Contact Information Section */}
                <Box sx={{ px: 2.5, py: 2 }}>
                    <SectionCard
                        title="Contact Information"
                        icon={<Icon icon="mdi:email-outline" width="20" height="20" />}
                    >
                        <InfoRow
                            icon={<EmailIcon fontSize="small" />}
                            label="Email Address"
                            value={getValue(user.email)}
                            isLink={true}
                        />
                        <InfoRow
                            icon={<LocationOnIcon fontSize="small" />}
                            label="City"
                            value={getValue(user.city)}
                        />
                    </SectionCard>

                    {/* User Details Section */}
                    <SectionCard
                        title="User Details"
                        icon={<Icon icon="mdi:account-outline" width="20" height="20" />}
                    >
                        <InfoRow
                            icon={<PersonIcon fontSize="small" />}
                            label="Full Name"
                            value={getValue(user.name)}
                        />
                        <InfoRow
                            icon={<Icon icon="mdi:account-key-outline" width="16" height="16" />}
                            label="Login ID"
                            value={getValue(user.loginId)}
                        />
                    </SectionCard>

                    {/* Login History Section */}
                    <SectionCard
                        title="Login History"
                        icon={<Icon icon="mdi:history" width="20" height="20" />}
                    >
                        <InfoRow
                            icon={<Icon icon="mdi:login" width="16" height="16" />}
                            label="Last Login"
                            value={getValue(user.lastLogin)}
                        />
                        <InfoRow
                            icon={<Icon icon="mdi:calendar-plus-outline" width="16" height="16" />}
                            label="Created On"
                            value={getValue(user.createdOn)}
                        />
                    </SectionCard>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default ViewUserDetailsModal;
